import { normalizeHeadingDepth } from '@heidi/answer-kit';
import { EvidenceCitationsButton, useClaimEvidenceCitationRuns } from '@heidi/evidence';
import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useMemo, type ComponentType, type ReactNode } from 'react';

import type { ChatItem } from '../../../contracts/harness.types';
import type { RefTarget } from '../../../contracts/projects/ref-tags';
import type { ProjectFeedbackRating } from '../../../contracts/projects/requests.types';
import {
  closeArtifactAtom,
  openArtifactAtom,
  type Artifact,
} from '../../../data/projects/artifacts/state';
import { extractLatestTurnEvidenceRunIds } from '../../../data/projects/extractLatestTurnEvidenceRunIds';
import { useSubmitProjectFeedback } from '../../../data/projects/hooks';
import { extractLatestTurnCopyableText } from '../../../data/projects/lib/extractLatestTurnCopyableText';
import { getWorkHostPort } from '../../work-home/work-home-ports';
import {
  ProjectMessageActions,
  type ProjectMessageActionsProps,
} from './ProjectMessageActions';
import {
  ResponseActionsReveal,
  type ResponseActionsVisibility,
} from './ResponseActionsReveal';

export interface ProjectReplyMessageActionsProps {
  projectId: string;
  /** Items of the turn being rated; copy text is taken from its latest turn. */
  displayItems: ChatItem[];
  /** Overrides the parent-turn prose, e.g. with an Evidence child's answer. */
  copyableText?: string | null;
  /**
   * The chat's resolved artefact opener. Passed only for the latest turn's
   * row, which offers that turn's citations; an earlier turn keeps its own
   * inline citations button.
   */
  onOpenArtifact?: (artifact: Artifact) => void;
  /** Transcript position of the reply, sent as the feedback `message_id`. */
  messageId: string;
  /** Ratings given this tab, keyed `projectId:messageId`. The row remounts
   *  when its reply stops being the latest (tail -> turn footer) or the chat is
   *  reopened, and the backend doesn't echo ratings back, so they're remembered. */
  givenRatings: Map<string, ProjectFeedbackRating>;
  visibility?: ResponseActionsVisibility;
  hideFeedback?: boolean;
}

/**
 * What the app supplies: the action row bound to its message-actioned emit, the
 * name a reference's chip resolved from the chat-object cache, and the pass that
 * strips Evidence's citation and image markup, which Evidence owns. Pass stable
 * values (a component, a cache reader, a module-level function).
 */
export interface ProjectReplyMessageActionsHost {
  MessageActions: ComponentType<ProjectMessageActionsProps>;
  labelFor: (target: RefTarget) => string | undefined;
  stripCitationMarkup: (text: string) => string;
  citations?: ReactNode;
}

/**
 * A Work reply's action row: resolves its copyable text, submits per-message
 * feedback and remembers the given rating across remounts, inside the
 * hover-reveal wrapper.
 */
export const ProjectReplyMessageActionsView = ({
  projectId,
  displayItems,
  copyableText,
  messageId,
  givenRatings,
  visibility = 'always',
  hideFeedback = false,
  MessageActions,
  labelFor,
  stripCitationMarkup,
  citations,
}: ProjectReplyMessageActionsProps & ProjectReplyMessageActionsHost) => {
  const submitFeedback = useSubmitProjectFeedback(projectId);
  const ratingKey = `${projectId}:${messageId}`;
  const resolvedCopyableText = useMemo(
    () =>
      copyableText !== undefined
        ? copyableText
        : extractLatestTurnCopyableText(displayItems, {
            labelFor,
            stripCitationMarkup,
            normalizeHeadingDepth,
          }),
    [copyableText, displayItems, labelFor, stripCitationMarkup]
  );

  const handleMessageFeedback = useCallback(
    async (rating: ProjectFeedbackRating, comment?: string) => {
      const response = await submitFeedback.mutateAsync({
        rating,
        message_id: messageId,
        user_feedback: comment ?? null,
      });
      givenRatings.set(ratingKey, rating);
      return response;
    },
    [submitFeedback, messageId, givenRatings, ratingKey]
  );

  return (
    <ResponseActionsReveal
      visibility={visibility}
      className="duration-300 animate-in fade-in"
    >
      <MessageActions
        key={messageId}
        projectId={projectId}
        copyableText={resolvedCopyableText}
        onSubmitFeedback={handleMessageFeedback}
        hideFeedback={hideFeedback}
        initialSelected={givenRatings.get(ratingKey) ?? null}
        citations={citations}
      />
    </ResponseActionsReveal>
  );
};

/** Binds the view to the app's action row, reference names and citation strippers. */
export const ProjectReplyMessageActions = (props: ProjectReplyMessageActionsProps) => {
  const labelFor = getWorkHostPort('evidence').useCachedReferenceLabel();
  const bridge = getWorkHostPort('bridge');
  return (
    <ProjectReplyMessageActionsView
      {...props}
      MessageActions={ProjectMessageActions}
      labelFor={labelFor}
      stripCitationMarkup={(text) => bridge.stripImgTags(bridge.stripCiteTags(text))}
      citations={
        props.onOpenArtifact ? (
          <ReplyCitationsAffordance
            displayItems={props.displayItems}
            onOpenArtifact={props.onOpenArtifact}
          />
        ) : null
      }
    />
  );
};

const NO_RUN_IDS: readonly string[] = [];

/** Latest-turn citations button. Mounted only when the row can open the surface. */
const ReplyCitationsAffordance = ({
  displayItems,
  onOpenArtifact,
}: {
  displayItems: ChatItem[];
  onOpenArtifact: (artifact: Artifact) => void;
}) => {
  const citationsEnabled =
    getWorkHostPort('shell').useIsWorkEvidenceCitationsEnabled?.() ?? false;
  const citationRunIds = useMemo(
    () => (citationsEnabled ? extractLatestTurnEvidenceRunIds(displayItems) : NO_RUN_IDS),
    [citationsEnabled, displayItems]
  );
  useClaimEvidenceCitationRuns(citationRunIds);
  const openArtifact = useAtomValue(openArtifactAtom);
  const closeArtifact = useSetAtom(closeArtifactAtom);
  const isOpen =
    openArtifact?.type === 'evidence-references' &&
    openArtifact.runIds.length === citationRunIds.length &&
    openArtifact.runIds.every((runId) => citationRunIds.includes(runId));

  if (citationRunIds.length === 0) return null;
  return (
    <EvidenceCitationsButton
      runIds={citationRunIds}
      isOpen={isOpen}
      onOpen={(runIds) =>
        isOpen ? closeArtifact() : onOpenArtifact({ type: 'evidence-references', runIds })
      }
      className="ms-1"
    />
  );
};
